/**
 * The player's two confirmations: «Выйти из тренировки?» (handoff §13) and «Начать
 * заново?» (§18). A bottom sheet on a phone, a centred 440 px dialog on desktop, as the
 * delete confirmation. Mounted only while open; Esc and the scrim are the secondary
 * action.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useIsDesktop } from '../../../hooks/useIsDesktop'
import { useSwipeNavBlock } from '../../../hooks/useSwipeNavBlock'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../../utils/controlSurface'

interface Action {
  label: string
  onClick: () => void
  testId?: string
}

export default function PlayerSheet({ title, children, primary, secondary, dismiss, danger = false, testId }: {
  title: string
  children: ReactNode
  /** The sheet's main action: accent fill, or the error fill when `danger`. */
  primary: Action
  /** What Esc does, and a tap or click on the scrim, on every width. Defaults to the secondary action. */
  secondary: Action
  /**
   * Overrides Esc and the desktop scrim when the secondary action is not the safe one: the
   * leave sheet's secondary is «Выйти», but Esc and a tap outside the sheet are «Продолжить
   * тренировку» (handoff §19.9; the phone follows the desktop, ADR-116 Implementation note 4).
   */
  dismiss?: () => void
  danger?: boolean
  testId?: string
}) {
  const desktop = useIsDesktop()
  useSwipeNavBlock(true, 'text-player-sheet')
  const escapeRef = useRef((dismiss ?? secondary.onClick))
  useEffect(() => { escapeRef.current = dismiss ?? secondary.onClick })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') escapeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const button = (filled: boolean, height: number): React.CSSProperties => ({
    height, padding: '0 20px', borderRadius: 'var(--radius-pill)', border: 'none', cursor: 'pointer',
    fontFamily: 'var(--font-ui)', fontSize: desktop ? 14 : 16, fontWeight: 600, width: desktop ? undefined : '100%',
    ...(filled
      ? { background: danger ? 'var(--danger-fill)' : 'var(--accent-fill)', color: danger ? 'var(--text-on-dark)' : 'var(--text-on-accent)' }
      : { background: 'var(--bg-secondary)', color: 'var(--text-primary)' }),
  })
  const dialog = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="player-sheet-title"
      data-testid={testId}
      onClick={(e) => e.stopPropagation()}
      style={desktop ? {
        background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow-float)',
        padding: 24, width: '100%', maxWidth: 440, fontFamily: 'var(--font-ui)',
      } : {
        position: 'absolute', left: 0, right: 0, bottom: 0, background: 'var(--bg-surface)', borderRadius: '16px 16px 0 0',
        boxShadow: 'var(--shadow-float)', fontFamily: 'var(--font-ui)', padding: '8px 20px max(20px, env(safe-area-inset-bottom))',
      }}
    >
      {!desktop && <div aria-hidden style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 16px' }} />}
      <h2 id="player-sheet-title" style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h2>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {children}
      </div>
      <div style={desktop
        ? { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }
        : { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}
      >
        {desktop ? (
          <>
            <button type="button" onClick={secondary.onClick} data-testid={secondary.testId} style={button(false, 40)}>{secondary.label}</button>
            <button type="button" onClick={primary.onClick} data-testid={primary.testId} style={button(true, 40)}>{primary.label}</button>
          </>
        ) : (
          <>
            <button type="button" onClick={primary.onClick} data-testid={primary.testId} style={button(true, 52)}>{primary.label}</button>
            <button type="button" onClick={secondary.onClick} data-testid={secondary.testId} style={button(false, 48)}>{secondary.label}</button>
          </>
        )}
      </div>
    </div>
  )
  return createPortal(
    <div
      className={`add-sheet-scrim ${CONTROL_SURFACE}`}
      onContextMenu={suppressTouchContextMenu}
      onClick={() => (dismiss ?? secondary.onClick)()}
      style={desktop
        ? { position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }
        : { position: 'fixed', inset: 0, zIndex: 200 }}
    >
      {dialog}
    </div>,
    document.body,
  )
}
